<template>
  <div class="podcast-panel">
    <SquareIconButton
      class="podcast-panel__close-btn"
      :icon="close"
      white
      @onClick="$emit('onClose')"
    />

    <div class="podcast-panel__super-title">
      {{ interviewee }}
    </div>
    <div class="podcast-panel__title">
      {{ title }}
    </div>
    <button class="podcast-panel__play-btn" @click="toggleAudio">
      <icon :data="playing ? pause : play" color="var(--primary-yellow)" />
    </button>

    <div id="waveform" class="podcast-panel__wave" />

    <div class="podcast-panel__metadata">
      <div>{{ duration }}</div>
      <div>{{ currentTime }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, inject, ref } from "vue";
import WaveSurfer from "wavesurfer.js";
import useTvaMq from "@/plugins/tvaMq.js";
import { play, pause, close } from "@/assets/icons/icons.js";

import SquareIconButton from "./SquareIconButton.vue";

const $baseUrl = inject("$baseUrl");
const { $tvaMq } = useTvaMq();
const smallscreen = computed(() => ["tablet", "mobile"].includes($tvaMq.value));

const wavesurfer = ref(null);
const playing = ref(true);

const durationInSeconds = ref(0);
const duration = computed(() => secondsToMinuteString(durationInSeconds.value));

const currentTimeInSeconds = ref(0);
const currentTime = computed(() =>
  secondsToMinuteString(currentTimeInSeconds.value)
);

const props = defineProps({
  title: String,
  audioFileName: String,
  interviewee: String,
});

onMounted(() => {
  wavesurfer.value = WaveSurfer.create({
    container: "#waveform",
    waveColor: "#ffd50080",
    progressColor: "#ffd500",
    barWidth: smallscreen.value ? 1 : 3,
    barHeight: 1, // the height of the wave
    height: smallscreen.value ? 50 : 60,
    barGap: null, // the optional spacing between bars of the wave, if not provided will be calculated in legacy format
  });

  wavesurfer.value.load(`${$baseUrl}audio/${props.audioFileName}.mp3`);

  wavesurfer.value.on("ready", function () {
    durationInSeconds.value = wavesurfer.value.backend.buffer.duration;

    duration.value = secondsToMinuteString(
      wavesurfer.value.backend.buffer.duration
    );
    wavesurfer.value.play();
  });

  wavesurfer.value.on("audioprocess", function () {
    currentTimeInSeconds.value = wavesurfer.value.getCurrentTime();
  });
});

onBeforeUnmount(() => wavesurfer.value.stop());

function toggleAudio() {
  if (playing.value) {
    wavesurfer.value.pause();
  } else {
    wavesurfer.value.play();
  }
  playing.value = !playing.value;
}

function secondsToMinuteString(timeInSeconds) {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(minutes ? timeInSeconds % 60 : timeInSeconds);

  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}
</script>

<style lang="scss" scoped>
.podcast-panel {
  --panel-padding: #{toRem(26)};
  --wave-height: 60px;

  background-color: var(--black-100);
  position: fixed;
  z-index: 2;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 20vh;
  color: var(--white-100);
  text-align: center;
  padding: var(--panel-padding) 0;

  .desktop &,
  .large & {
    z-index: 6;
  }

  &__close-btn {
    position: absolute;
    right: 1.5rem;
    top: 1.5rem;
    z-index: 1;

    .mobile &,
    .tablet & {
      right: 0.5rem;
      top: 0.5rem;
    }

    ::v-deep(.svg-icon path) {
      fill: var(--white-100);
    }
  }

  &__super-title {
    @include dida-s;

    opacity: 0.7;
  }

  &__title {
    @include title-s;
    padding-bottom: 1rem;
  }

  &__play-btn {
    position: absolute;
    top: calc(var(--panel-padding) + var(--wave-height) * 0.5 + 3.25rem);
    left: calc(25% - 3rem);

    .mobile & {
      top: calc(var(--panel-padding) + var(--wave-height) * 0.5 + 4.75rem);
      left: 1.5rem;
    }
  }

  &__wave {
    cursor: pointer;

    .desktop &,
    .large & {
      width: 50%;
      min-width: toRem(420);
      margin: 0 auto;
    }

    .mobile &,
    .tablet & {
      width: calc(100% - 5rem);
      margin: 0 1.5rem 0 auto;
    }
  }

  &__metadata {
    padding-top: 0.75rem;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    font-family: "EniTabBold";
    font-size: toRem(12);
    line-height: toRem(13);
    letter-spacing: -0.005em;

    .desktop &,
    .large & {
      width: 50%;
      min-width: toRem(420);
    }

    .mobile &,
    .tablet & {
      width: 100%;
      padding: 0 1.5rem;
    }
  }
}
</style>
